import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
// Lazy load the ToggleSwitch component
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showNotification } from "aldoo-ra/Notification"
import { SendUserSiteContact } from "aldoo-ra/ContactPanel/api"
import PubSub from "pubsub-js"
import emailValidator from "email-validator"

const ContactPanel = () => {
  const [questionType, setQuestionType] = useState("")
  const [message, setMessage] = useState("")
  const [email, setEmail] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [file, setFile] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const fileInputRef = useRef(null)

  useEffect(() => {
    const token = PubSub.subscribe("open-contacts", () => {
      setIsOpen(true)
    })
    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  //reset the form when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      setQuestionType("")
      setMessage("")
      setEmail("")
      setAgreed(false)
      setFile(null)
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      setErrors({})
      setSubmitError(null)
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleQuestionTypeChange = (e) => {
    setQuestionType(e.target.value)
  }

  const handleMessageChange = (e) => {
    setMessage(e.target.value)
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
  }

  const handleAgreementChange = (value) => {
    setAgreed(value)
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const handleFileInputClick = (e) => {
    // Clear the input value before opening file picker
    // This allows selecting the same file again
    e.target.value = ""
  }

  const validateForm = () => {
    const errors = {}
    if (!questionType) errors.questionType = `Please select a question type.`
    if (!message) errors.message = `Please enter a message.`

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      errors.email = `Please enter an email.`
    } else {
      // Check if email format is valid using email-validator
      if (!emailValidator.validate(trimmedEmail)) {
        errors.email = `Please enter a valid email address.`
      }
    }
    if (!agreed) errors.agreed = `You must agree to the terms.`

    //if there is a file and it's more than 20MB, show an error
    if (file && file.size > 20 * 1024 * 1024) {
      errors.file = `File size must be less than 20MB.`
    }

    return errors
  }

  const handleSubmit = async () => {
    setSubmitError(null)

    // Explicit email validation check
    const trimmedEmail = email.trim()
    if (!trimmedEmail || !emailValidator.validate(trimmedEmail)) {
      setErrors({ email: !trimmedEmail ? 'Please enter an email.' : 'Please enter a valid email address.' })
      return
    }

    const validationErrors = validateForm()

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors({})

    setIsSubmitting(true)
    try {
      const payload = {
        questionType,
        message,
        email,
        file,
      }

      const response = await SendUserSiteContact(payload)

      if (response.error) {
        setSubmitError(`Server error: ${response.error}`)
        setIsSubmitting(false)
        return
      }

      if (response.result) {
        showNotification({
          message: `Thank you! We'll review your message immediately.`,
          className: "bg-primary text-admin_text_inv",
        })
        // Clear file input before closing
        setFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
        setIsOpen(false)
      }
    } catch (error) {
      setSubmitError(
        `An error occurred while submitting your message. Please try again.`
      )
    }
    setIsSubmitting(false)
  }

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false)
      }}
      className="sm:w-[500px] sm:max-w-[700px] h-full md:h-auto"
      zIndex={410}
      title={`Contact Us`}
    >
      <div>
        <div>
          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.questionType ? "text-error" : "text-admin_text_secondary"
              }`}
            >
              {`Ask a Question`}
            </label>
            <select
              value={questionType}
              onChange={handleQuestionTypeChange}
              className={`text-admin_text bg-admin_neutral_1 border ${
                errors.questionType ? "border-error" : "border-admin_bordered"
              } rounded p-2 w-full`}
            >
              <option value="">{`Select an option`}</option>
              <option value="report_issue">{`Report an Issue`}</option>
              <option value="ask_question">{`Ask a Question`}</option>
              <option value="suggest_feature">{`Suggest a Feature`}</option>
              <option value="contact_sales">{`Contact Sales`}</option>
            </select>
          </div>

          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.message ? "text-admin_error" : "text-admin_text_secondary"
              }`}
            >
              {`Message`}
            </label>
            <textarea
              value={message}
              onChange={handleMessageChange}
              className={`outline-none text-admin_text bg-admin_neutral_2 border ${
                errors.message ? "border-admin_error" : "border-admin_bordered"
              } rounded p-2 w-full`}
              rows="4"
            ></textarea>
          </div>

          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.email ? "text-admin_error" : "text-admin_text_secondary"
              }`}
            >
              {`Email`}
            </label>

            <Input
              variant="admin"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className={`outline-none text-admin_text bg-admin_neutral_2 border ${
                errors.email ? "border-admin_error" : "border-admin_bordered"
              } rounded p-2 w-full`}
            />
          </div>

          <div className="mb-4">
            <label className="block font-bold text-admin_text_secondary mb-2">
              {`Attach File (optional)`}
            </label>

            <Input
              variant="admin"
              type="file"
              ref={fileInputRef}
              onClick={handleFileInputClick}
              onChange={handleFileChange}
              className="text-admin_text bg-admin_neutral_2 border border-admin_bordered rounded p-2 w-full"
            />
            {file && (
              <p className="text-admin_text_secondary text-sm mt-2">
                {`Attached: ${file.name}`}
              </p>
            )}
            {errors.file && (
              <p className="text-error text-sm mt-2">{errors.file}</p>
            )}
          </div>

          <div className="mt-8 mb-6">
            <div className="flex items-center gap-4 text-admin_text_secondary">
              <ToggleSwitch isOn={agreed} onChange={handleAgreementChange} />
              <Typography>
                {`I agree to the`}{" "}
                <a href="/terms-of-use" className="text-admin_link">
                  {`Terms of Service`}
                </a>{" "}
                {`and`}{" "}
                <a href="/privacy-policy" className="text-admin_link">
                  {`Privacy Policy`}
                </a>
              </Typography>
            </div>
            <div
              className={`${
                errors.agreed ? "visible" : "invisible"
              } w-full mt-2 h-0.5 bg-admin_error`}
            ></div>
          </div>
          <div className="mb-4">
            <Button
              onClick={handleSubmit}
              variant="admin_primary"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? `Sending...` : `Send`}
            </Button>
            {Object.keys(errors).length > 0 && (
              <p className="text-admin_error mb-4 text-center">
                {`Please correct the highlighted fields above.`}
              </p>
            )}
            {submitError && <p className="text-admin_error">{submitError}</p>}
          </div>
        </div>
        <Typography className="w-full text-center text-sm text-admin_text_secondary">
          {`All files are treated confidentially.`}
        </Typography>
      </div>
    </Modal>
  )
}

export default ContactPanel
